import axios from "axios";
import { toast } from "react-toastify";
import { getToken, resetUser } from "./auth";
import { API_BASE_URL } from "../common/apiUrls";

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "x-source-country": "CA",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add auth token to headers
    const token = getToken();
    if (token) {
      config.headers["x-auth-token"] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Handle success messages
    if (response.data.action === 1 && response.data.message !== "ok") {
      toast.success(response.data.message);
    }
    return response;
  },
  (error) => {
    // Handle error responses
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors
      // The session ended (logged out elsewhere, password changed, account
      // disabled): log in again.
      if (status === 401) {
        resetUser();
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
        return Promise.reject(new Error("Authentication failed"));
      }

      // Handle validation errors
      if (status === 400) {
        const errorMessage = data.message || "Invalid request";
        toast.error(errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      // Handle server errors
      if (status >= 500) {
        toast.error("Server error occurred");
        return Promise.reject(new Error("Server error"));
      }

      // Handle other errors
      const errorMessage = data.message || "An error occurred";
      toast.error(errorMessage);
      return Promise.reject(new Error(errorMessage));
    }

    // Handle network errors
    if (error.request) {
      toast.error("Network error occurred");
      return Promise.reject(new Error("Network error"));
    }

    // Handle other errors
    toast.error("An unexpected error occurred");
    return Promise.reject(error);
  },
);

// Helper function to handle form data
const handleFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key]) {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

// API request methods
export const api = {
  get: async (url, params = {}) => {
    try {
      const response = await axiosInstance.get(url, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data = {}) => {
    try {
      const response = await axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  put: async (url, data = {}) => {
    try {
      const response = await axiosInstance.put(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (url) => {
    try {
      const response = await axiosInstance.delete(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  upload: async (url, formData) => {
    try {
      const response = await axiosInstance.post(url, handleFormData(formData), {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
